import { Phone, Mail, MapPin, MessageCircle, Accessibility, Instagram, Facebook } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

export const Footer = ({ onOpenLegalPage }) => {
  const { language, translations: t } = useLanguage();

  // Social media URLs - update these when ready
  const SOCIAL_LINKS = {
    whatsapp: "https://wa.me/972523824500",
    instagram: "https://www.instagram.com/mazalganeel",
    facebook: "https://www.facebook.com/mazalganeel"
  };

  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const target = document.getElementById(href.substring(1));
      if (target) {
        const headerOffset = 100;
        const offsetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }
  };
  
  return (
    <footer className="bg-[#2C2C2C] text-white py-16" data-testid="footer" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/e84fca2b-7e76-4834-b103-9bf7c18b7fcd/images/2d9573f2a9fc4e9c08165186a64d94d93f2109c8aeba2abfd768c1e7a458adbc.png" 
                alt="Mazal Logo" 
                className="h-14 w-auto rounded-lg"
                width="56"
                height="56"
                loading="lazy"
                decoding="async"
              />
              <div>
                <span className="font-['Heebo'] text-2xl font-bold">{language === 'he' ? 'מזל' : 'Mazal'}</span>
                <span className="block text-[#C4956A] text-sm">{language === 'he' ? 'קוסמטיקאית פרא-רפואית' : 'Paramedical Cosmetician'}</span>
              </div>
            </div>
            <p className="text-white/60 mb-6">{t.footer.description}</p>
            
            <div className="p-3 bg-white/5 rounded-lg text-xs text-white/50 leading-relaxed">
              {language === 'he' 
                ? 'המידע באתר הוא למטרות מידע כללי בלבד ואינו מהווה תחליף לייעוץ רפואי מקצועי.'
                : 'Information on this site is for general purposes only and is not a substitute for professional medical advice.'}
            </div>
            
            <div className="flex gap-4 mt-4">
              <a href={SOCIAL_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#25D366] transition-colors" aria-label={language === 'he' ? "צרו קשר בוואטסאפ" : "Contact us on WhatsApp"} data-testid="footer-whatsapp">
                <MessageCircle className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E4405F] transition-colors" aria-label={language === 'he' ? "עקבו אחרינו באינסטגרם" : "Follow us on Instagram"} data-testid="footer-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1877F2] transition-colors" aria-label={language === 'he' ? "עקבו אחרינו בפייסבוק" : "Follow us on Facebook"} data-testid="footer-facebook">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-['Heebo'] font-bold text-lg mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-3 text-white/60">
              <li><a href="#about" onClick={(e) => handleNavClick(e, '#about')} className="hover:text-[#C4956A] transition-colors">{t.nav.about}</a></li>
              <li><a href="#treatments" onClick={(e) => handleNavClick(e, '#treatments')} className="hover:text-[#C4956A] transition-colors">{t.nav.treatments}</a></li>
              <li><a href="#results" onClick={(e) => handleNavClick(e, '#results')} className="hover:text-[#C4956A] transition-colors">{t.nav.results}</a></li>
              <li><a href="#testimonials" onClick={(e) => handleNavClick(e, '#testimonials')} className="hover:text-[#C4956A] transition-colors">{t.nav.testimonials}</a></li>
              <li><a href="#faq" onClick={(e) => handleNavClick(e, '#faq')} className="hover:text-[#C4956A] transition-colors">{language === 'he' ? 'שאלות נפוצות' : 'FAQ'}</a></li>
              <li><a href="#contact" onClick={(e) => handleNavClick(e, '#contact')} className="hover:text-[#C4956A] transition-colors">{t.nav.contact}</a></li>
              <li><a href="/blog" className="hover:text-[#C4956A] transition-colors">{language === 'he' ? 'מאמרים מקצועיים' : 'Blog'}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-['Heebo'] font-bold text-lg mb-4">{language === 'he' ? 'מידע משפטי' : 'Legal'}</h4>
            <ul className="space-y-3 text-white/60">
              <li>
                <button onClick={() => onOpenLegalPage('privacy')} className="hover:text-[#C4956A] transition-colors">
                  {t.footer.legal.privacy}
                </button>
              </li>
              <li>
                <button onClick={() => onOpenLegalPage('terms')} className="hover:text-[#C4956A] transition-colors">
                  {t.footer.legal.terms}
                </button>
              </li>
              <li>
                <button onClick={() => onOpenLegalPage('cookies')} className="hover:text-[#C4956A] transition-colors">
                  {t.footer.legal.cookies}
                </button>
              </li>
              <li>
                <button onClick={() => onOpenLegalPage('accessibility')} className="hover:text-[#C4956A] transition-colors flex items-center gap-2">
                  <Accessibility className="w-4 h-4" />
                  {t.footer.legal.accessibility}
                </button>
              </li>
              <li>
                <button
                  onClick={() => window.dispatchEvent(new Event('openCookieSettings'))}
                  className="hover:text-[#C4956A] transition-colors"
                  data-testid="footer-manage-cookies"
                >
                  {language === 'he' ? 'ניהול עוגיות' : 'Manage Cookies'}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-['Heebo'] font-bold text-lg mb-4">{t.footer.contact}</h4>
            <ul className="space-y-3 text-white/60">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C4956A]" />
                <a href="tel:052-3824500" className="hover:text-[#C4956A] transition-colors">052-3824500</a>
              </li>
              <li className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                <a href="https://wa.me/972523824500" target="_blank" rel="noopener noreferrer" className="hover:text-[#25D366] transition-colors">{t.contact.info.whatsappText}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#C4956A]" />
                <a href="mailto:Mazal41400@gmail.com" className="hover:text-[#C4956A] transition-colors">Mazal41400@gmail.com</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#C4956A]" />
                <span>{language === 'he' ? 'חיים גבריהו 11, פסגת זאב, ירושלים' : 'Chaim Gevriahu 11, Pisgat Zeev, Jerusalem'}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="p-4 bg-white/5 rounded-xl text-xs text-white/50 leading-relaxed mb-6">
            <p className="mb-2">
              <strong className="text-white/70">הבהרה חשובה:</strong> השירותים המוצעים באתר זה הם טיפולים קוסמטיים פרא-רפואיים ואינם מהווים טיפול רפואי.
              מזל גניאל היא קוסמטיקאית פרא-רפואית מוסמכת ואינה רופאה. הטיפולים אינם מחליפים ייעוץ או טיפול רפואי מקצועי.
            </p>
            <p>
              התוצאות המוצגות באתר משתנות מאדם לאדם ואינן מהוות הבטחה לתוצאה זהה.
              לכל שאלה רפואית יש לפנות לרופא/ה מוסמך/ת.
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/40 text-sm">
            <p>© 2026 {language === 'he' ? 'מזל גניאל — כל הזכויות שמורות.' : 'Mazal Ganeel — All rights reserved.'}</p>
            <p className="text-white/30">{language === 'he' ? 'ע.פ. 057931883' : 'Licensed Business 057931883'}</p>
            <p>{language === 'he' ? 'התוצאות משתנות בין אדם לאדם. הטיפולים אינם תחליף לייעוץ רפואי.' : 'Results vary. Treatments are not a substitute for medical advice.'}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
